import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { track, trackViewItemList } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Check, Download, Loader2, ShieldCheck, Heart, Sparkles } from "lucide-react";

export default function Faith() {
  const [data, setData] = useState(null);
  useEffect(() => {
    track.faithCollectionViewed();
    api.get("/faith/collection").then((r) => {
      setData(r.data);
      const list = [r.data.flagship_digital, ...(r.data.coming_soon || [])].filter(Boolean);
      trackViewItemList("Faith Collection", list);
    }).catch(() => setData({}));
  }, []);

  if (!data) return <div className="min-h-[60vh] flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-amber" /></div>;

  const { collection, flagship_digital: digital, flagship_printed: printed, coming_soon = [] } = data;
  const digitalReady = digital?.published && digital?.download_asset;

  return (
    <div className="bg-cream" data-testid="faith-page">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={digital?.images?.[1] || digital?.images?.[0]} alt="" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/70 via-cream/85 to-cream" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28 text-center">
          <span className="inline-flex items-center gap-2 text-amber-700 font-semibold text-sm tracking-wide uppercase">
            <Sparkles className="w-4 h-4" /> {collection?.name}
          </span>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slateink mt-4 leading-tight">
            Faith that fits little hands
          </h1>
          <p className="text-slate-600 text-base md:text-lg mt-5 max-w-2xl mx-auto leading-relaxed">
            {collection?.positioning}
          </p>
          <div className="mt-8">
            <Button asChild data-testid="faith-explore-cta" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold px-8 h-12 text-base">
              <a href="#flagship">Explore the Faith Collection</a>
            </Button>
          </div>
        </div>
      </section>

      {/* Flagship */}
      <section id="flagship" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 scroll-mt-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center bg-white rounded-3xl border border-slate-100 p-6 sm:p-10 shadow-sm" data-testid="faith-flagship">
          <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-subtle">
            <img src={digital?.images?.[0]} alt={digital?.name} className="w-full h-full object-cover" />
            <Badge className="absolute top-4 left-4 bg-amber text-white border-0 font-semibold">Flagship</Badge>
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-700/70">{collection?.name}</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slateink mt-1 leading-tight">{digital?.name}</h2>
            <p className="text-slate-500 mt-1">{digital?.subtitle}</p>
            <p className="text-slate-600 mt-4 leading-relaxed">{digital?.description}</p>

            {digital?.whats_included?.length > 0 && (
              <ul className="mt-5 space-y-2">
                {digital.whats_included.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                    <Check className="w-4 h-4 text-sage mt-0.5 shrink-0" /> {w}
                  </li>
                ))}
              </ul>
            )}

            <div className="flex items-baseline gap-2 mt-6">
              <span className="font-display text-3xl font-bold text-slateink">${Number(digital?.starting_price || 0).toFixed(0)}</span>
              <span className="text-slate-400 text-sm">digital edition</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 mt-4">
              {digitalReady ? (
                <Button asChild data-testid="faith-get-digital" className="rounded-full bg-amber hover:bg-amber-600 text-white font-semibold h-12 px-6">
                  <Link to={`/product/${digital.slug}`}><Download className="w-4 h-4 mr-2" /> Get the Digital Book</Link>
                </Button>
              ) : (
                <Button disabled data-testid="faith-digital-soon" className="rounded-full bg-slate-200 text-slate-500 font-semibold h-12 px-6 cursor-not-allowed">
                  Digital Book — Coming Soon
                </Button>
              )}
              <Button disabled data-testid="faith-printed-soon" variant="outline" className="rounded-full h-12 px-6 border-slate-200 text-slate-400 cursor-not-allowed">
                Printed Book — Coming Soon
              </Button>
            </div>

            <p className="text-xs text-slate-400 mt-4 flex items-start gap-1.5">
              <BookOpen className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {collection?.attribution}
            </p>
          </div>
        </div>

        {/* Trust row */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 mt-8">
          {(collection?.trust || []).map((t, i) => (
            <div key={i} className="flex items-center gap-2 bg-white rounded-xl border border-slate-100 px-4 py-3 text-sm text-slate-600">
              <ShieldCheck className="w-4 h-4 text-sage shrink-0" /> {t}
            </div>
          ))}
        </div>
      </section>

      {/* Coming soon */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        <div className="flex items-center gap-2 mb-6">
          <Heart className="w-5 h-5 text-amber" />
          <h2 className="font-display text-2xl font-bold text-slateink">More faith keepsakes on the way</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="faith-coming-soon-grid">
          {coming_soon.map((p) => (
            <div key={p.slug} data-testid={`faith-coming-soon-${p.slug}`} className="rounded-2xl overflow-hidden bg-white border border-slate-100">
              <div className="relative aspect-[4/5] overflow-hidden bg-subtle">
                <img src={p.images?.[0]} alt={p.name} loading="lazy" className="w-full h-full object-cover opacity-90" />
                <Badge className="absolute top-3 left-3 bg-slate-900/70 text-white border-0 font-semibold">Coming Soon</Badge>
              </div>
              <div className="p-4">
                <h3 className="font-display text-base font-bold text-slateink leading-tight">{p.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{p.subtitle}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
